/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { Fragment as PMFragment } from '@atlaskit/editor-prosemirror/model';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';
import { isMarkdownEndingWithCustomTag } from '@atlassian/ai-model-io/utils/is-markdown-with-custom-tag';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { MemoLoadingScreen } from '@atlassian/generative-ai-modal/screens/Loading';

import type { AIPlugin } from '../../../../aiPluginType';
import type { StreamFns } from '../../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useAIUsageDisclaimer } from '../../../hooks/useAIUsageDisclaimer';
import { useLocalizedLatestPrompt } from '../../../hooks/useLocalizedLatestPrompt';
import { usePromptStatistics } from '../../../hooks/usePromptStatistics';
import { getTranslatedPromptLabel } from '../../../prebuilt/config-items/config-items';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { UI_SCROLL_MARGIN_TOP } from '../../../utils/constants';
import {
	convertADFResponseToPreview,
	convertPMFragmentToPreview,
} from '../../../utils/convert-markdown-to-preview';
import { getPMFragmentWithFallback } from '../../../utils/get-pm-fragment-with-fallback';
import { adfResponseToFragment } from '../../../utils/request/adf-response';
import type {
	AIExperienceMachineContext,
	ContextStatistics,
	LatestPrompt,
	PromptTrigger,
} from '../../../utils/xstate/get-ai-experience-service';

// Selectors will be used for editorView.dom.closest() to track
// the scroll parent element and scrollable content height
const AK_EDITOR_CONTENT_AREA_SELECTOR = '.ak-editor-content-area';

/**
 * Scroll margin is used to ensure that the modal won't be
 * too close to the top of the editor, which may sometimes
 * cover the modal slightly.
 */
const scrollMarginStyles = css({ scrollMarginTop: token('space.400') });

const gridStyles = css({ display: 'grid' });

export function LoadingScreenWithLogic({
	mountDecorations,
	updateStream,
	parentPresetPromptLabel,
	childShortTitle,
	title,
	latestPrompt,
	promptTrigger,
	refinementCount,
	latestPromptRetried,
	markdown,
	adfResponse,
	adfResponseNodeWrappers,
	idMap,
	contextStatistics,
	hideLoadingScreen,
	api,
}: {
	/**
	 * When prompt supports communication in ADF, adfReponse will have actual LLM response
	 * 	and markdown will be empty string.
	 */
	adfResponse?: DocNode;
	/**
	 * During ADF streaming, there may be multiple node wrappers that need added to the Slice.
	 * We need to remove these node wrappers from the Slice before inserting it to the doc.
	 */
	adfResponseNodeWrappers?: AIExperienceMachineContext['adfResponseNodeWrappers'];
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	childShortTitle?: string;
	contextStatistics?: ContextStatistics;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	idMap?: IDMap;
	latestPrompt?: LatestPrompt;
	latestPromptRetried: boolean;
	markdown?: string;
	mountDecorations?: StreamFns['mountDecorations'];
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	promptTrigger?: PromptTrigger;
	/**
	 * This is used to track the number of times the user has refined
	 * (clicked by Refine button in the Preview screen) the prompt.
	 */
	refinementCount?: number;
	title: string;
	updateStream?: StreamFns['updateStream'];
}): JSX.Element | null {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const {
		configItem,
		editorView,
		providerFactory,
		sendToAIExperienceMachine,
		editorPluginAIProvider,
		includeKnowledgeFromCurrentPage,
		isUserScroll,
		isProgrammaticScroll,
		appearance,
	} = aiExperienceCommonData;
	const schema = editorView.state.schema;
	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	useEffect(() => {
		if (mountDecorations) {
			return mountDecorations();
		}
		return () => {};
	}, [mountDecorations]);

	const { mounted: isRovoMounted, streaming: isRovoStreaming } = useUIStore();

	const { formatMessage } = useIntl();
	const disclaimer = useAIUsageDisclaimer({ editorPluginAIProvider });

	const presetPromptLabel = getTranslatedPromptLabel(configItem, formatMessage);

	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const [preview, setPreview] = useState<JSX.Element>();
	const [hasPreview, setHasPreview] = useState<boolean>(false);

	const containerRef = useRef<HTMLDivElement>(null);

	// raf is used for debouncing, if useEffect is called
	// multiple times before raf is run this prevents rendering
	// multiple times if we receive streams very quickly
	const raf = useRef<number | undefined>();
	const errorReported = useRef<boolean>(false);

	/**
	 * scrollParentRef is for
	 * storing the scroll parent element once its found
	 * to be re-used in calculations
	 */
	const scrollParentRef = useRef<HTMLElement | null>(null);
	const initialScrollTop = useRef<number>(0);
	const modalOffsetTop = useRef<number>(0);
	const isModalAtTop = useRef<boolean>(false);

	const scrollModalAlignBottom = useCallback(
		(modalEl: HTMLElement, scrollParentEl: HTMLElement) => {
			// We set isProgrammaticScroll to true to flag it as a non-user scroll.
			isProgrammaticScroll.current = true;
			scrollParentEl.scroll({
				top:
					initialScrollTop.current +
					modalOffsetTop.current +
					modalEl.scrollHeight -
					scrollParentEl.offsetHeight,
				behavior: 'instant',
			});
		},
		[isProgrammaticScroll],
	);

	const scrollModalToTop = useCallback(
		(scrollParentEl: HTMLElement) => {
			// We set isProgrammaticScroll to true to flag it as a non-user scroll.
			isProgrammaticScroll.current = true;
			scrollParentEl.scroll({
				top: initialScrollTop.current + modalOffsetTop.current - UI_SCROLL_MARGIN_TOP,
				behavior: 'instant',
			});
		},
		[isProgrammaticScroll],
	);

	useEffect(() => {
		/**
		 * Set up ResizeObserver to detect cases where the editor content grows,
		 * as it will normally trigger a scroll event due to scrollHeight and scrollTop changes.
		 * We set isProgrammaticScroll to true to flag it as a non-user scroll.
		 */
		const editorContentArea: HTMLElement | null = editorView.dom.closest(
			AK_EDITOR_CONTENT_AREA_SELECTOR,
		);
		if (!editorContentArea) {
			return;
		}
		const resizeObserver = new ResizeObserver(() => {
			isProgrammaticScroll.current = true;
		});
		resizeObserver.observe(editorContentArea);
		return () => resizeObserver.unobserve(editorContentArea);
	}, [editorView, isProgrammaticScroll]);

	/**
	 * Editor AI can be invoked directly into this Loading screen.
	 * ExperienceApplication calls scrollIntoView
	 *
	 * We need to check whether scrollIntoView is complete to determine when to track user scrolling for the auto-scroll logic
	 * that prevents scroll-jacking when a user scrolls away.
	 */
	// const [isScrollIntoViewSettled, setIsScrollIntoViewSettled] = useState(false);
	useEffect(() => {
		const modalEl = containerRef.current;
		if (!modalEl) {
			return;
		}
		const scrollParentEl: HTMLElement | false = findOverflowScrollParent(modalEl);
		if (!scrollParentEl) {
			return;
		}

		const settleScroll = debounce(() => {
			// setIsScrollIntoViewSettled(true);
			/**
			 * Once it's settled, we can unbind
			 * as we only need to check when it first loads
			 *
			 * This is to emulate scrollEnd which is not supported
			 * across all browsers at the moment.
			 *
			 * unbind taken from the bind function below
			 */
			unbind();
		}, 250);

		const unbind = bind(scrollParentEl, { type: 'scroll', listener: settleScroll });
	}, []);

	function scrollModalIntoView() {
		/**
		 * Early exits:
		 * - If user has manually scrolled, stop auto-scrolling
		 * - If modal has reached the top of the editor, no more calculations or scrolling needed
		 */
		if (isUserScroll.current || isModalAtTop.current) {
			return;
		}
		const modalEl = containerRef.current;
		if (!modalEl) {
			return;
		}
		const scrollParentEl = scrollParentRef.current;
		if (!scrollParentEl) {
			return;
		}

		// Check if modal is already fully visible
		const isModalFullyVisible =
			scrollParentEl.offsetHeight > modalOffsetTop.current + modalEl.scrollHeight;

		/**
		 * Check if modal height exceeds scroll parent's height
		 * Scroll margin is used to prevent the modal from being scrolled to the top
		 * and should match scroll margin in other modals e.g. from
		 * packages/editor/editor-plugin-ai/src/ui/components/FloatingContainer/styles.ts
		 */
		const isModalFullHeight =
			scrollParentEl.offsetHeight - UI_SCROLL_MARGIN_TOP < modalEl.scrollHeight;

		if (isModalFullyVisible) {
			return;
		}

		if (isModalFullHeight) {
			scrollModalToTop(scrollParentEl);
			isModalAtTop.current = true;
			return;
		}

		scrollModalAlignBottom(modalEl, scrollParentEl);
	}

	const adfResponseRef = useRef(adfResponse);
	adfResponseRef.current = adfResponse;
	const pmFragmentRef = useRef<PMFragment | undefined>(undefined);

	useEffect(() => {
		if (adfResponseRef.current && updateStream) {
			const pmFragment = adfResponseToFragment({
				schema: schema,
				adfResponse: adfResponseRef.current,
				adfResponseNodeWrappers,
			});
			// don't update stream if the pmFragment is the same.
			if (!pmFragmentRef.current || !pmFragmentRef.current.eq(pmFragment)) {
				pmFragmentRef.current = pmFragment;
				isProgrammaticScroll.current = true;
				updateStream({
					fragment: pmFragment,
					isFinalStream: false,
					hasUserScrolled: isUserScroll.current,
				});
				/**
				 * Setting hasPreview to true as AI generated content has started streaming in page now.
				 * hasPreview is used to set durations (time to receive first chunk and first to remaining chunks).
				 */
				setHasPreview(true);
			}
			return;
		}

		// only run the callback if the raf has not been set, and then
		// reset raf to undefined
		if (raf.current === undefined) {
			raf.current = window.requestAnimationFrame(() => {
				let _preview;
				raf.current = undefined;

				if (adfResponseRef.current) {
					if (updateStream) {
						const pmFragment = adfResponseToFragment({
							schema: schema,
							adfResponse: adfResponseRef.current,
							adfResponseNodeWrappers,
						});

						if (!pmFragmentRef.current || !pmFragmentRef.current.eq(pmFragment)) {
							pmFragmentRef.current = pmFragment;
							isProgrammaticScroll.current = true;

							updateStream({
								fragment: pmFragment,
								isFinalStream: false,
								hasUserScrolled: isUserScroll.current,
							});
						}

						/**
						 * Setting hasPreview to true as AI generated content has started streaming in page now.
						 * hasPreview is used to set durations (time to receive first chunk and first to remaining chunks).
						 */
						setHasPreview(true);
					} else {
						_preview = convertADFResponseToPreview({
							adfResponse: adfResponseRef.current,
							showTelepointer: true,
							providerFactory,
						});
					}
				} else if (!markdown || markdown === '') {
					// When there is no streamed preview content -- set the preview to
					// undefined.
					// This allows a nicer loading screen (rather than showing an empty paragraph).
					_preview = undefined;
				} else {
					const { pmFragment } = getPMFragmentWithFallback({
						markdown: markdown,
						schema,
						source: 'loading',
						fireAIAnalyticsEvent: (event) => {
							if (!errorReported.current) {
								errorReported.current = true;
								fireAIAnalyticsEvent(event);
							}
						},
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						idMap: idMap!,
					});

					/**
					 * Hold off on setting the preview if we appear to be receiving
					 * a custom tag through our stream. This is to hide raw tags from
					 * users until the tags are complete and can be rendered back into nodes.
					 */
					if (isMarkdownEndingWithCustomTag(markdown)) {
						return;
					}
					if (updateStream) {
						isProgrammaticScroll.current = true;
						updateStream({
							fragment: pmFragment,
							isFinalStream: false,
							hasUserScrolled: isUserScroll.current,
						});
					} else {
						_preview = convertPMFragmentToPreview({
							schema,
							pmFragment,
							showTelepointer: true,
							providerFactory,
						});
					}
				}
				if (_preview) {
					setPreview(_preview);
					setHasPreview(true);
					window.requestAnimationFrame(() => {
						scrollModalIntoView();
					});
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [markdown, updateStream, adfResponse]);

	useEffect(() => {
		return () => {
			// if the component is unmounted before the raf callback
			// has run, we cancel it
			if (raf.current) {
				window.cancelAnimationFrame(raf.current);
			}
		};
	}, []);

	// preset prompt or base generate / interrogation
	const localizedLatestPrompt = useLocalizedLatestPrompt(latestPrompt);
	const promptStatistics = usePromptStatistics(localizedLatestPrompt);

	const analyticsFlow = useAnalyticsFlowEventsQueue({
		needUpdatedArgs: true,
		stepName: 'loading',
		callerComponent: 'LoadingScreenWithLogic',
		attributes: {
			promptType: promptTrigger,
			latestPromptRetried,
			refinementCount,
			modelIO: {
				contextStatistics,
				userInputCharacterCount: promptStatistics?.totalCharacterCount,
				userInputNodeCounts: promptStatistics?.nodeCounts,
				userInputLinksCount: promptStatistics?.linksCount,
				userInputMentionsCount: promptStatistics?.mentionsCount,
			},
			includeKnowledgeFromCurrentPage,
		},
	});

	const previewBecameAvailable = useRef<number>();

	if (!previewBecameAvailable.current && hasPreview) {
		previewBecameAvailable.current = performance.now();
	}

	useEffect(() => {
		const noPreviewAvailable = performance.now();

		return () => {
			if (!previewBecameAvailable.current) {
				return;
			}

			analyticsFlow.addAttributes({
				durations: {
					timeSpentWithoutPartialPreview: previewBecameAvailable.current - noPreviewAvailable,
					timeSpentWithPartialPreview: performance.now() - previewBecameAvailable.current,
				},
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onCancel = useCallback(() => {
		sendToAIExperienceMachine({ type: 'enable discard' });
	}, [sendToAIExperienceMachine]);

	const isRovoStreamingActive = isRovoMounted && isRovoStreaming;

	const shouldHideLoadingScreen = isRovoStreamingActive || !!hideLoadingScreen;

	if (nextSessionId) {
		return null;
	}

	return (
		<div
			ref={containerRef}
			css={[
				scrollMarginStyles,
				!expValEquals('platform_editor_ai_screen_layout_refactor', 'isEnabled', true) && gridStyles,
			]}
		>
			<MemoLoadingScreen
				parentPresetPromptLabel={parentPresetPromptLabel}
				childShortTitle={childShortTitle}
				presetPromptLabel={presetPromptLabel}
				latestPrompt={localizedLatestPrompt}
				agent={configItem?.agent}
				title={title}
				preview={preview}
				providerFactory={providerFactory}
				disclaimer={disclaimer}
				onCancel={onCancel}
				schema={schema}
				isStreaming={!!adfResponse || !!markdown}
				canInlineStreamChanges
				hideLoadingScreen={shouldHideLoadingScreen}
				appearance={appearance}
				isAIFC
				loadingExperience={configItem?.loadingExperience}
			/>
		</div>
	);
}
